import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook que ejecuta una carga asíncrona y, con intervaloMs > 0, la refresca en silencio.
 * `cargar` debe ser estable (useCallback): al cambiar se descartan las respuestas en vuelo,
 * se reinicia el intervalo y se vuelve a cargar con loading.
 * @param {Function} cargar - Función asíncrona que devuelve los datos
 * @param {{ intervaloMs?: number, valorInicial?: any }} [opciones]
 * @returns {{ datos: any, setDatos: Function, loading: boolean, error: (string|null), recargar: Function }}
 */
const useCargaPeriodica = (cargar, { intervaloMs, valorInicial = [] } = {}) => {
    const [datos, setDatos] = useState(valorInicial);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Se incrementa al cambiar `cargar` o desmontar: invalida respuestas en vuelo
    const versionRef = useRef(0);
    const enVueloRef = useRef(false);

    const ejecutar = useCallback(async (silencioso) => {
        const version = versionRef.current;
        enVueloRef.current = true;
        if (!silencioso) setLoading(true);
        try {
            const resultado = await cargar();
            if (version !== versionRef.current) return;
            setDatos(resultado);
            setError(null);
        } catch (e) {
            if (version !== versionRef.current) return;
            setError(e.message);
        }
        enVueloRef.current = false;
        setLoading(false);
    }, [cargar]);

    const recargar = useCallback(() => ejecutar(false), [ejecutar]);

    useEffect(() => {
        const version = ++versionRef.current;
        enVueloRef.current = false;
        ejecutar(false);
        const temporizador = intervaloMs > 0
            ? setInterval(() => {
                if (!enVueloRef.current) ejecutar(true);
            }, intervaloMs)
            : null;
        return () => {
            if (temporizador) clearInterval(temporizador);
            if (versionRef.current === version) versionRef.current++;
        };
    }, [ejecutar, intervaloMs]);

    return { datos, setDatos, loading, error, recargar };
};

export default useCargaPeriodica;
